import { create } from 'zustand'
import { persist } from 'zustand/middleware'

/** Widget ids in the order the design lists them under "Edit widgets". */
export const WIDGET_IDS = [
  'news',
  'events',
  'tools',
  'navigator',
  'calendar',
  'contacts',
  'studentOffice',
  'doItOnline',
  'discount',
  'myStudies',
  'scholarships',
] as const

export type WidgetId = (typeof WIDGET_IDS)[number]

interface WidgetsState {
  /** Display order; the dashboard renders the enabled ones in this order. */
  order: WidgetId[]
  enabled: WidgetId[]
  toggle: (id: WidgetId) => void
  move: (from: number, to: number) => void
  reset: () => void
}

const DEFAULT_ENABLED: WidgetId[] = [
  'news',
  'events',
  'tools',
  'calendar',
  'doItOnline',
  'discount',
]

/**
 * Dashboard widget selection and order — Figma "Edit widgets"
 * (2265:290022). Persisted locally until a preferences endpoint exists.
 */
export const useWidgetsStore = create<WidgetsState>()(
  persist(
    (set) => ({
      order: [...WIDGET_IDS],
      enabled: DEFAULT_ENABLED,
      toggle: (id) => {
        set((state) => ({
          enabled: state.enabled.includes(id)
            ? state.enabled.filter((entry) => entry !== id)
            : [...state.enabled, id],
        }))
      },
      move: (from, to) => {
        set((state) => {
          if (from === to) return state
          const next = [...state.order]
          const [moved] = next.splice(from, 1)
          if (!moved) return state
          next.splice(to, 0, moved)
          return { order: next }
        })
      },
      reset: () => {
        set({ order: [...WIDGET_IDS], enabled: DEFAULT_ENABLED })
      },
    }),
    { name: 'corvinus-widgets' },
  ),
)
